import type { ReactNode } from "react";

import type { AdoptionPin, ProjectAdoptions } from "@/api/library";
import type {
  SummaryReportSection,
  SummaryStructureRegistry,
} from "@/api/summaryStructure";
import {
  SUMMARY_CATEGORY_LABEL,
  SUMMARY_MAPPING_AUTHORITY_LABEL,
  SUMMARY_MAPPING_STATE_LABEL,
  SUMMARY_OWNERSHIP_LABEL,
} from "@/lib/labels";
import {
  effectiveSummarySource,
  pinForAsset,
  sectionOrderDiffers,
  sectionsInDisplayOrder,
  uniformLanguageDirection,
  type SummaryPromptRef,
} from "@/lib/summaryStructure";
import { Badge } from "@/ui/Badge";

import { Identity } from "./SummaryIdentity";

/**
 * The report structure: the server's twelve sections, what each maps to in the
 * runtime projection, and the presentation fields declared for it (#640).
 *
 * ## Compact by construction
 *
 * One row per section, and every row is identity, mapping and ownership — never
 * content. The reference product's Summary Composer is twelve sections of
 * fonts, sizes and reorder buttons disconnected from anything real; the
 * deployed projection is 172 read-only pack values whose bodies a Project route
 * may not render at all (#488). This surface is the join the audit found
 * missing, and it is a join of IDENTITIES: a section slug, a prompt identity, a
 * runtime coordinate, an asset id, a pinned version. No prompt body and no
 * runtime value passes through here, which is what keeps the ticket's
 * "compact schema-driven presentation instead of huge raw prompt/runtime
 * bodies" true mechanically rather than by restraint.
 *
 * ## Three honest mapping shapes, and never a fourth
 *
 * #639 emits exactly three, and this component renders exactly three:
 *
 *  - `not_established` — no authority connects this section to the runtime.
 *    Rendered as that sentence. The parity audit's own finding is that the
 *    reference prototype "does not connect those layers", so an invented
 *    correspondence here would be fabricating the very thing the audit
 *    recorded as absent.
 *  - `established` with no `runtimeKey` — a recorded identity pairing whose
 *    prompt is base-inherited. The pairing is shown; no runtime coordinate and
 *    no library asset is shown, because the server states none.
 *  - `established` with a `runtimeKey` and a `seededAssetId` — the seed catalog
 *    proves a placed asset at an exact coordinate. Both are shown, with the
 *    project's exact pin when it holds one.
 *
 * ## Identity, not navigation
 *
 * A mapped asset is named by its exact id and never linked. One asset gets one
 * `View in Clinical library` per page (#570, extended to exact navigation by
 * #615), and on this screen that control belongs to the pin card and the
 * section adoption panel. Exact Project-to-library navigation is #615's ticket,
 * not this one's; adding a third route to the same asset here would be a
 * second answer to a question that already has one.
 */
export function SummaryReportStructure({
  registry,
  adoptions,
  summaryPrompts,
}: {
  registry: SummaryStructureRegistry;
  /**
   * The project's pin set, or `undefined` while it is loading or after it
   * failed.
   *
   * `undefined` is a real state and is rendered as one: a mapped asset whose
   * pin is unknown says so, rather than reading as unpinned. The value is the
   * Clinical Summary's single adoptions query, passed down like every other
   * consumer on that screen so three views of one pin set cannot disagree.
   */
  adoptions: ProjectAdoptions | undefined;
  /**
   * The exact Summary-prompt pins the RUNTIME PROJECTION says supplied this
   * project's Summary (`GET /sections/summary`'s `summaryPrompts`), or
   * `undefined` while that projection is unread or failed.
   *
   * This — not the pin set — is what decides a section's effective source. An
   * authored copy placed at base canon's own runtime key supplies the section
   * and pins a DIFFERENT id from the seeded one the registry names, so a join
   * on the pin set alone called the section the base pack's while the
   * projection attributed its value to the copy.
   */
  summaryPrompts?: readonly SummaryPromptRef[] | undefined;
}) {
  const sections = sectionsInDisplayOrder(registry);
  const languageDirection = uniformLanguageDirection(registry);
  const unmappedCount = sections.filter(
    (section) => section.runtimeMapping.state === "not_established",
  ).length;

  return (
    <section className="space-y-3" aria-labelledby="summary-report-structure">
      <div className="space-y-1">
        <h3
          id="summary-report-structure"
          className="text-sm font-semibold text-foreground"
        >
          Report structure — {sections.length}{" "}
          {sections.length === 1 ? "section" : "sections"}
        </h3>
        <p className="text-sm text-muted-foreground">
          The report&apos;s sections in the order the presentation layer
          declares, each with the runtime values it maps to. Identities only —
          no prompt text and no runtime value is shown here or anywhere on this
          screen.
        </p>
        {/*
          Said ONCE, above the list, rather than on each of the sections that
          carry it.

          Most sections are unmapped in the structure this deployment serves,
          and repeating a three-line explanation on each of them made the panel
          scroll for a paragraph a reader has already read — the opposite of the
          compact presentation this ticket exists to deliver. The badge on each
          row still states the fact per section, so nothing is lost from a row
          read on its own; what moves up here is the reason, which is the same
          sentence every time because it is a property of the CONTRACT and not
          of any one section.
        */}
        {unmappedCount > 0 ? (
          <p className="text-sm text-muted-foreground">
            {unmappedCount === 1
              ? "One section is marked "
              : `${unmappedCount} sections are marked `}
            <span className="font-medium text-foreground">
              {SUMMARY_MAPPING_STATE_LABEL.not_established}
            </span>
            : no authority connects them to the runtime projection. The Editor
            Service states that absence rather than inventing a correspondence,
            so nothing here claims which runtime values produce them.
          </p>
        ) : null}
        {languageDirection ? (
          <p className="text-sm text-muted-foreground">
            Language and direction are{" "}
            {SUMMARY_OWNERSHIP_LABEL[languageDirection.ownership].toLowerCase()}{" "}
            for every section and derived from the preview target. No control
            here or in the Clinical library sets them per section.
          </p>
        ) : null}
      </div>

      <ul className="space-y-3">
        {sections.map((section) => (
          <li key={section.slug}>
            <SectionRow
              section={section}
              adoptions={adoptions}
              summaryPrompts={summaryPrompts}
              showLanguageDirection={languageDirection === null}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function SectionRow({
  section,
  adoptions,
  summaryPrompts,
  showLanguageDirection,
}: {
  section: SummaryReportSection;
  adoptions: ProjectAdoptions | undefined;
  summaryPrompts: readonly SummaryPromptRef[] | undefined;
  /**
   * Set when the registry's sections do NOT share one language/direction
   * policy, so the shared sentence above the list could not be written. Then
   * every row states its own — the contract carries the policy per section
   * precisely so that stays possible.
   */
  showLanguageDirection: boolean;
}) {
  const { runtimeMapping: mapping } = section;
  const pin = pinForAsset(adoptions, mapping.seededAssetId);

  return (
    <div className="space-y-2 rounded-md border border-border p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {/*
          The ordinal is direction-pinned for `Identity`'s reason, in its
          smallest form: "1." is a digit followed by a neutral, and under
          `dir="rtl"` that full stop resolves to the paragraph direction and
          renders on the wrong side of the number. Isolated too, so it does not
          drag the slug beside it.
        */}
        <span
          dir="ltr"
          className="[unicode-bidi:isolate] text-sm font-medium tabular-nums text-muted-foreground"
        >
          {section.presentation.order}.
        </span>
        {/*
          `basis-full` below the small breakpoint: at 375px the slug, the
          mapping badge and the ownership badge cannot share one line, and a
          `flex-1` identity squeezed between them wraps mid-word
          (`backgro/undHist/ory`) because `wrap-anywhere` is what keeps a long
          route template from overflowing the viewport. Giving the identity its
          own line on a narrow screen keeps both properties: no horizontal
          overflow, and no identity broken across three lines.
        */}
        <Identity className="min-w-0 flex-1 basis-full text-sm sm:basis-auto">
          {section.slug}
        </Identity>
        <Badge tone={mapping.state === "established" ? "accent" : "outline"}>
          {SUMMARY_MAPPING_STATE_LABEL[mapping.state]}
        </Badge>
        {mapping.ownership ? (
          <Badge tone="neutral">
            {SUMMARY_OWNERSHIP_LABEL[mapping.ownership]}
          </Badge>
        ) : null}
      </div>

      {sectionOrderDiffers(section) ? (
        // Two orders exist and they mean different things. `position` is the
        // clinically meaningful structural order (ratified §2.1: explicit,
        // versioned data); `presentation.order` is display state. They agree at
        // rest, and if they ever stop agreeing a reader must be able to see
        // that the clinical order did not move.
        <p className="text-xs text-muted-foreground">
          Structural position in the report is {section.position}; the
          presentation layer displays it at {section.presentation.order}.
        </p>
      ) : null}

      {/*
        An unmapped section renders its badge and nothing else: the reason is
        the same for every one of them and is stated once above the list.
      */}
      {mapping.state === "not_established" ? null : (
        <MappingDetail
          section={section}
          pin={pin}
          adoptions={adoptions}
          summaryPrompts={summaryPrompts}
        />
      )}

      {showLanguageDirection ? (
        <p className="text-xs text-muted-foreground">
          Language and direction:{" "}
          {SUMMARY_OWNERSHIP_LABEL[
            section.languageDirection.ownership
          ].toLowerCase()}
          , derived from the preview target.
        </p>
      ) : null}
    </div>
  );
}

function MappingDetail({
  section,
  pin,
  adoptions,
  summaryPrompts,
}: {
  section: SummaryReportSection;
  pin: AdoptionPin | undefined;
  adoptions: ProjectAdoptions | undefined;
  summaryPrompts: readonly SummaryPromptRef[] | undefined;
}) {
  const { runtimeMapping: mapping } = section;
  // COMPOSED OWNERSHIP decides the effective source: the projection's own
  // refs, joined on the runtime key (an authored owner) or on the seeded id
  // (base canon's own row). The pin set below only adds what the project
  // PINS, which is not the same claim.
  const source = effectiveSummarySource(mapping, summaryPrompts);

  return (
    <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2">
      {mapping.category ? (
        <Cell label="Runtime value category">
          {SUMMARY_CATEGORY_LABEL[mapping.category]}
        </Cell>
      ) : null}

      {mapping.promptIdentity ? (
        <Cell label="Prompt identity">
          <Identity>{mapping.promptIdentity}</Identity>
        </Cell>
      ) : null}

      <Cell label="Runtime coordinate">
        {mapping.runtimeKey ? (
          <Identity>{mapping.runtimeKey}</Identity>
        ) : (
          <span className="text-muted-foreground">
            None. The correspondence is recorded, but this section&apos;s prompt
            is inherited from the base pack and the Editor Service invents no
            coordinate for it.
          </span>
        )}
      </Cell>

      <Cell label="Effective source">
        {source.kind === "unmapped" ? (
          <span className="text-muted-foreground">
            The base pack. No Clinical library asset is placed at this section,
            and none is fabricated to fill it.
          </span>
        ) : source.kind === "unread" ? (
          <span className="text-muted-foreground">
            This project&apos;s runtime projection has not been read, so which
            version supplies this section&apos;s prompt is not claimed.
            {adoptions === undefined ? null : pin === undefined ? (
              <>
                {" "}
                Its pin set does not pin{" "}
                <Identity>{mapping.seededAssetId}</Identity>.
              </>
            ) : (
              <>
                {" "}
                Its pin set pins <Identity>{mapping.seededAssetId}</Identity>{" "}
                at <Identity className="text-foreground">{pin.version}</Identity>.
              </>
            )}
          </span>
        ) : source.kind === "adopted" ? (
          <span>
            Library asset <Identity>{source.assetId}</Identity> at{" "}
            <Identity className="text-foreground">{source.version}</Identity>{" "}
            supplies this section&apos;s prompt: the runtime projection
            attributes the value at this coordinate to that exact pin.
            {mapping.seededAssetId != null &&
            source.assetId !== mapping.seededAssetId ? (
              <>
                {" "}
                It replaces the base pack&apos;s{" "}
                <Identity>{mapping.seededAssetId}</Identity> at the same
                runtime placement.
              </>
            ) : null}{" "}
            {/*
              WHERE the decision lives, not what is on screen beside it.

              An earlier wording said "the adoption controls above", which is a
              claim about the rendered page — and the adoption block states its
              own read failures, so on a deployment whose section projection is
              unavailable that sentence points at controls a clinician cannot
              see. What is true in every state is whose decision it is: adopting,
              updating and removing an exact pin is the project's adoption
              contract, and this panel is a reader of that decision.
            */}
            Adopting, updating and removing that exact pin are the project
            adoption&apos;s decision; nothing on this panel moves it.
          </span>
        ) : (
          <span className="text-muted-foreground">
            The base pack. This project&apos;s runtime projection attributes
            no adopted version to this section&apos;s prompt
            {mapping.seededAssetId != null ? (
              <>
                {" "}
                — <Identity>{mapping.seededAssetId}</Identity>, the library
                asset placed at this coordinate, is not pinned here
              </>
            ) : null}
            .
          </span>
        )}
      </Cell>

      {/*
        `authorities` is optional on the wire — the response omits an empty
        list, since the document is served with nulls and empties dropped so a
        client can recompute its digest from what it received. `?? []` is
        therefore reading the contract, not defending against it: an absent list
        and an empty one are the same fact, and the contract states which fact
        that is ("Empty if and only if state is not_established").
      */}
      {(mapping.authorities ?? []).length > 0 ? (
        <Cell label="Mapping authority" className="sm:col-span-2">
          <ul className="space-y-0.5">
            {(mapping.authorities ?? []).map((authority) => (
              <li key={authority}>
                {SUMMARY_MAPPING_AUTHORITY_LABEL[authority]}
              </li>
            ))}
          </ul>
        </Cell>
      ) : null}
    </dl>
  );
}

function Cell({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className ? `min-w-0 ${className}` : "min-w-0"}>
      <dt className="font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-foreground">{children}</dd>
    </div>
  );
}
